import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { INVESTOR_ALLOWED_ROUTES as INVESTOR_ALLOWED, DISTRIBUTOR_ALLOWED_ROUTES as DISTRIBUTOR_ALLOWED } from "@/lib/permissions";
import SplashLoader from "@/components/common/SplashLoader";

const landingFor = (u) => {
  if (u?.active_role === "investor") return "/investor";
  if (u?.active_role === "distributor") return "/distributor";
  return "/dashboard";
};

export default function ProtectedRoute({ children, allow }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <SplashLoader />;
  }
  if (!user) return <Navigate to="/login" replace />;

  // Security gates — apply BEFORE role/route checks.
  // Force password change first, then MFA enrolment.
  if (user.must_change_password && location.pathname !== "/security/change-password") {
    return <Navigate to="/security/change-password" replace />;
  }
  if (user.must_enroll_mfa && !user.must_change_password
      && location.pathname !== "/security"
      && location.pathname !== "/security/change-password") {
    return <Navigate to="/security" replace />;
  }

  // Investor role is restricted to /investor/* routes only
  if (user.active_role === "investor") {
    const ok = INVESTOR_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/investor" replace />;
  }

  // Distributor role is restricted to /distributor/* + profile/security/help
  if (user.active_role === "distributor") {
    const ok = DISTRIBUTOR_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/distributor" replace />;
  }

  // Other roles cannot access /investor portal (match exact /investor or /investor/*, NOT /investors)
  if (user.active_role !== "investor" && (location.pathname === "/investor" || location.pathname.startsWith("/investor/"))) {
    return <Navigate to="/dashboard" replace />;
  }

  // Non-distributor roles cannot access /distributor/* portal routes
  if (user.active_role !== "distributor"
      && (location.pathname === "/distributor" || location.pathname.startsWith("/distributor/"))) {
    return <Navigate to="/dashboard" replace />;
  }

  if (allow && !allow.includes(user.active_role) && !user.roles?.includes("super_admin")) {
    return <Navigate to={landingFor(user)} replace />;
  }
  return children;
}
